import { logAppError } from 'Helpers/logger'
import {
  addSavedRow,
  countSavedRows,
  getBeeMessageToken,
  getBeePluginConfig,
  getDynamicFieldsWhitelist,
  getSavedRow,
  getThirdPartyAccepted,
  getUsedTokens,
  setDynamicFieldsWhitelist,
  setThirdPartyResourceAlert,
  storeUsedTokens,
  updateSavedRows
} from '../actions/actions'
import { beePluginState, editorState, openAIState } from '../state/editor'
import {
  modalSaveNewAndRenameState,
  modalThirdPartyState
} from '../state/modals'
import { pageErrorState, pageState } from '../state/page'
import getIntl from 'Helpers/intl'
import { MSG_PLACEHOLDERS_SPECIAL_LINKS } from 'Constants/emailPlaceholders'
import { STORE_KEY_LOGIN_TYPE, storeGet } from 'mailup-react-helpers'
import {
  LOGIN_TYPE_WEBLOGIN,
  MAX_SAVED_ROW_NUMBER,
  STORE_KEY_ID_LIST
} from 'Constants/index'
import {
  TOAST_VARIANT_ERROR,
  TOAST_VARIANT_SUCCESS,
  toastShow
} from 'mailup-web-components'
import { useHistory } from 'react-router-dom'
import { pendoTrack } from 'Helpers/pendoTracking'

export const checkForThirdPartyCallback = ({ set }) => async () => {
  const thirdPartyAccepted = await getThirdPartyAccepted()

  set(editorState, prev => ({
    ...prev,
    thirdPartyAccepted
  }))

  set(modalThirdPartyState, prev => ({
    ...prev,
    show: !thirdPartyAccepted
  }))
}

export const addMergeTagToWhitelistCallback = ({
  set,
  snapshot
}) => async tag => {
  const { mergeTagsWhitelist, templateDetails } = await snapshot.getPromise(
    editorState
  )
  const newMergeTagsWhitelist = new Set(mergeTagsWhitelist).add(tag)

  try {
    setDynamicFieldsWhitelist('NL', templateDetails.id, [
      ...newMergeTagsWhitelist
    ])
    set(editorState, prev => ({
      ...prev,
      mergeTagsWhitelist: [...newMergeTagsWhitelist]
    }))
  } catch (error) {
    logAppError({ appError: error })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
}

export const getTotalTokensCountCallback = ({ set }) => async () => {
  try {
    const resp = await getUsedTokens()
    set(openAIState, prev => ({
      ...prev,
      totalTokens: resp.TotalTokens,
      remainingTokens: resp.RemainingTokens,
      enableUse: resp.EnableUse,
      showBuy: resp.ShowBuy,
      loaded: true
    }))
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'RowEdit',
      Method: 'getTotalTokensCountCallback'
    })
  }
}

export const storeUsedTokensCounterCallback = () => async value => {
  try {
    await storeUsedTokens(value)
  } catch (error) {
    logAppError({
      appError: error,
      Component: 'RowEdit',
      Method: 'storeUsedTokensCounterCallback'
    })
  }
}

export const initBeePluginRowCallback = ({ set, snapshot }) => async ({
  id,
  settings
}) => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  const onLoad = () => {
    set(beePluginState, prev => ({ ...prev, loaded: true }))
  }

  const onError = err => {
    console.error('###err', err)
    logAppError({
      appError: err,
      Component: 'RowEdit',
      Method: 'BeePluginEditor',
      Message: 'Error triggered by Bee Plugin'
    })
  }

  try {
    const token = await getBeeMessageToken()

    const config = await getBeePluginConfig()

    const dynamicFieldsWhitelist = await getDynamicFieldsWhitelist('TPL', id)

    const savedRow = await getSavedRow(id)
    const rowDetails = {
      category: savedRow.IdCategory,
      hasThumbnail: false,
      id: savedRow.IdRow,
      thumbnail: null,
      title: savedRow.Name,
      type: null,
      structure: JSON.parse(savedRow.Json)
    }

    set(editorState, prev => ({
      ...prev,
      rowDetails,
      mergeTags: [
        ...config.mergeTags.map(({ name }) => name),
        ...MSG_PLACEHOLDERS_SPECIAL_LINKS
      ],
      mergeTagsWhitelist: dynamicFieldsWhitelist
    }))

    set(pageState, prev => ({
      ...prev,
      loading: false,
      title: savedRow.Name
    }))

    const beeConfig = {
      ...config,
      onLoad,
      onError,
      ...settings
    }

    window.BeePlugin.create(token, beeConfig, instance => {
      set(beePluginState, prev => ({ ...prev, instance }))
      const template = {
        page: {
          body: {},
          rows: [rowDetails.structure]
        }
      }
      instance.start(template)
    })
  } catch (error) {
    logAppError({
      appError: error
    })
    set(pageErrorState, {
      error: getIntl().formatMessage({
        id: 'Load.Bee.EditorInitializationError'
      }),
      severity: 'danger'
    })
    set(pageState, prev => ({
      ...prev,
      loading: false
    }))
  }
}

export const updateThirdPartyCallback = ({ set, reset }) => async () => {
  set(editorState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    const loginType = storeGet(STORE_KEY_LOGIN_TYPE)
    if (loginType === LOGIN_TYPE_WEBLOGIN) {
      await setThirdPartyResourceAlert()
    }
    set(editorState, prev => ({
      ...prev,
      thirdPartyAccepted: true
    }))

    reset(modalThirdPartyState)
  } catch (error) {
    logAppError({
      appError: error
    })
    set(pageErrorState, {
      error: getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      severity: 'danger'
    })
  }
  set(editorState, prev => ({
    ...prev,
    loading: false
  }))
}

export const addNewSaveRowCallback = ({
  set,
  snapshot,
  reset
}) => async json => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))
  try {
    const { name } = await snapshot.getPromise(modalSaveNewAndRenameState)
    const formattedJson = JSON.parse(json)
    const row = formattedJson.page.rows[0]

    const structure = {
      ...row,
      metadata: {
        ...row.metadata,
        name: name
      }
    }

    const resp = await addSavedRow({
      Name: name,
      Json: JSON.stringify(structure),
      IdCategory: 0
    })
    reset(modalSaveNewAndRenameState)
    set(editorState, prev => ({
      ...prev,
      idNewRow: resp.Id
    }))
  } catch (error) {
    logAppError({ appError: error })
    set(editorState, prev => ({
      ...prev,
      saved: false
    }))
    toastShow(
      getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      TOAST_VARIANT_ERROR
    )
    reset(modalSaveNewAndRenameState)
  }
}

export const renameSaveRowCallback = ({
  set,
  snapshot,
  reset
}) => async name => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))
  try {
    const { rowDetails } = await snapshot.getPromise(editorState)

    const json = {
      ...rowDetails.structure,
      metadata: {
        ...rowDetails.structure.metadata,
        name: name
      }
    }

    await updateSavedRows(rowDetails.id, {
      Name: name,
      Json: JSON.stringify(json)
    })

    set(pageState, prev => ({
      ...prev,
      title: name
    }))
    toastShow(
      getIntl().formatMessage({
        id: 'Load.SaveRow.ToasSaveSuccess'
      }),
      TOAST_VARIANT_SUCCESS,
      'ToasSaveSuccess',
      true
    )
  } catch (error) {
    logAppError({ appError: error })
    set(editorState, prev => ({
      ...prev,
      saved: false
    }))
    toastShow(
      getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      TOAST_VARIANT_ERROR
    )
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
  reset(modalSaveNewAndRenameState)
}

export const saveRowCallback = ({ set, snapshot }) => async (json, isNew) => {
  let exit = false
  const { rowDetails, exitAfertSave } = await snapshot.getPromise(editorState)

  set(pageState, prev => ({
    ...prev,
    loading: true
  }))

  try {
    set(editorState, prev => ({
      ...prev,
      saved: true
    }))

    if (!isNew) {
      await updateSavedRows(rowDetails.id, {
        Name: rowDetails.title,
        Json: JSON.stringify(JSON.parse(json).page.rows[0])
      })
    } else {
      await addSavedRow({
        Name: rowDetails.title,
        Json: JSON.stringify(JSON.parse(json).page.rows[0]),
        IdCategory: 0
      })
      //
    }

    exit = exitAfertSave

    toastShow(
      getIntl().formatMessage({
        id: 'Load.SaveRow.ToasSaveSuccess'
      }),
      TOAST_VARIANT_SUCCESS,
      'ToasSaveSuccess',
      true
    )
  } catch (error) {
    exit = false
    logAppError({ appError: error })
    set(editorState, prev => ({
      ...prev,
      saved: false
    }))
    toastShow(
      getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      TOAST_VARIANT_ERROR
    )
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
  return exit
}

export const checkCountSavedRowsCallback = ({ set }) => async () => {
  set(pageState, prev => ({
    ...prev,
    loading: true
  }))
  let ret
  try {
    const respCountSavedRows = await countSavedRows(0)

    if (respCountSavedRows.Count >= MAX_SAVED_ROW_NUMBER) {
      pendoTrack('Editor bee - New row exceed', {
        DateTime: new Date().toISOString(),
        IdList: storeGet(STORE_KEY_ID_LIST),
        Mode: 'edit-save-row'
      })

      toastShow(
        getIntl().formatMessage({ id: 'Load.Bee.SaveRowMaxNumberReach' }),
        TOAST_VARIANT_ERROR,
        'SaveRowMaxNumberReach'
      )

      ret = false
    } else {
      ret = true
    }
  } catch (error) {
    logAppError({ appError: error })
    toastShow(
      getIntl().formatMessage({ id: 'Load.Alerts.ErrorGeneric' }),
      TOAST_VARIANT_ERROR
    )
    ret = false
  }

  set(pageState, prev => ({
    ...prev,
    loading: false
  }))
  return ret
}
